import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import Stack, { type StackProps } from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { type ReactNode, useMemo } from 'react';
import { Avatar, type AvatarProps } from '../avatar';

type SelectCustomVariant = Omit<TextFieldProps, 'variant'> & {
  variant?:
    | TextFieldProps['variant']
    | 'white-filled'
    | 'white-filled-outlined'
    | 'soft';
};

export type SelectProps<TSelectOption> = SelectCustomVariant & {
  options: TSelectOption[];
  getOptionLabel?: (option: TSelectOption) => string | ReactNode;
  customSelectRef?: TextFieldProps['ref'];
  optionIdKey?: keyof TSelectOption;
  optionTextKey?: TSelectOption extends object ? keyof TSelectOption : never;
  renderValue?: (option: TSelectOption) => ReactNode;
  renderAvatarOption?: (
    option: TSelectOption,
    renderOption: (
      avatarProps: AvatarProps & {
        caption?: string | null;
        captionProps?: TypographyProps;
        hideAvatar?: boolean;
      },
      stackProps?: StackProps,
    ) => React.ReactNode,
  ) => React.ReactNode;
  menuItemProps?: MenuItemProps;
  loading?: boolean;
  headerComponent?: ReactNode;
};

export const Select = <TSelectOption extends string | number | object>({
  options,
  optionIdKey,
  optionTextKey,
  customSelectRef,
  getOptionLabel,
  variant,
  sx,
  SelectProps,
  renderValue,
  value,
  menuItemProps,
  loading,
  headerComponent,
  renderAvatarOption,
  slotProps,
  ...textFieldProps
}: SelectProps<TSelectOption>) => {
  const { spacing, palette } = useTheme();

  const isWhiteFilledVariant =
    variant === 'white-filled' || variant === 'white-filled-outlined';
  const isSoftVariant = variant === 'soft';
  const customVariant = isWhiteFilledVariant || isSoftVariant;

  const optionSelected = useMemo(
    () =>
      options.find((currentOption) => {
        const currentValue = optionIdKey
          ? (currentOption[optionIdKey] as string)
          : String(currentOption);

        return currentValue === value;
      }),
    [options, optionIdKey, value],
  );

  return (
    <TextField
      {...textFieldProps}
      value={value}
      variant={customVariant ? 'filled' : variant}
      ref={customSelectRef}
      select
      slotProps={{
        ...slotProps,
        // @ts-expect-error
        select: {
          ...SelectProps,
          ...slotProps?.select,
          MenuProps: {
            ...SelectProps?.MenuProps,
            sx: {
              maxHeight: spacing(44),
              ...SelectProps?.MenuProps?.sx,
            },
          },
          ...(renderValue && {
            renderValue: () =>
              optionSelected ? (
                <Stack flexDirection="row" gap={1} alignItems="center">
                  {loading && <CircularProgress size={16} />}
                  {renderValue(optionSelected)}
                </Stack>
              ) : null,
          }),
        },
      }}
      sx={{
        ...sx,
        ...(isWhiteFilledVariant && {
          '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root.Mui-focused, & .MuiFilledInput-root:hover, & .MuiFilledInput-root, & .MuiFilledInput-input:focus, & .MuiFilledInput-input.Mui-focused, & .MuiFilledInput-input:hover':
            {
              backgroundColor: palette.background.default,
              borderRadius: spacing(1),
            },
          ...(variant === 'white-filled-outlined' && {
            '& .MuiFilledInput-root': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '& > input': {
                pt: '24px',
                pb: '7px',
              },
            },
            '& .MuiInputBase-root:hover': {
              backgroundColor: palette.primary.lighter,
            },
          }),
        }),
        ...(isSoftVariant && {
          '& .MuiSelect-select.MuiSelect-filled.MuiInputBase-input.MuiFilledInput-input':
            {
              paddingRight: spacing(5),
            },
          '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root.Mui-focused, & .MuiFilledInput-root:hover, & .MuiFilledInput-root, & .MuiFilledInput-input:focus, & .MuiFilledInput-input.Mui-focused, & .MuiFilledInput-input:hover':
            {
              backgroundColor: palette.indigo[50016],
              color: palette.indigo.dark,
              borderRadius: spacing(1),
            },
        }),
      }}
    >
      {headerComponent && (
        <Stack gap={1} component="li" mx={1}>
          {headerComponent}
          <Divider sx={{ borderColor: 'slate.200' }} />
        </Stack>
      )}
      {options.map((option) => {
        const optionValue = optionIdKey
          ? (option[optionIdKey] as string)
          : String(option);
        const optionLabel = optionTextKey
          ? (option[optionTextKey] as string)
          : getOptionLabel?.(option);

        return (
          <MenuItem key={optionValue} value={optionValue} {...menuItemProps}>
            {renderAvatarOption
              ? renderAvatarOption(
                  option,
                  (
                    { caption, captionProps, hideAvatar, ...avatarProps },
                    stackProps,
                  ) => (
                    <Stack
                      direction="row"
                      gap={2}
                      alignItems="center"
                      {...stackProps}
                    >
                      {hideAvatar ? null : (
                        <Avatar size={32} {...avatarProps} />
                      )}
                      <Stack>
                        <Typography variant="subtitle2">
                          {avatarProps.name}
                        </Typography>
                        {caption && (
                          <Typography variant="caption" {...captionProps}>
                            {caption}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ),
                )
              : (optionLabel ?? optionValue)}
          </MenuItem>
        );
      })}
    </TextField>
  );
};
